/**
 * App catalog + lab jobs API (Image Builder 2.1, #1711/#1712).
 *
 * Catalog: GET /apps, GET /apps/{appId}, POST /apps, PUT /apps/{appId}/{version},
 * POST /apps/{appId}/{version}/verify. Lab jobs (blob proxy, viewer):
 * GET /image-jobs/{jobId}, /events?after=, /bundles/{appId}.
 */

import apiClient from './api';
import { ApiResponse, AppRecipe, AppSummary } from './types';

/** POST/PUT body: a recipe without the server-owned fields (the API schema is strict). */
export type AppRecipeInput = Omit<AppRecipe, 'id' | 'pk' | 'status' | 'verification' | 'registeredBy' | 'createdAt' | 'modifiedAt'>;

export interface VerifyResult {
  dispatchId: string;
  recipes: string[];
  jobUrl: string | null;
}

/** One line of the agent's events.jsonl (CampusAgent; fields vary by phase). */
export interface LabEvent {
  ts?: string;
  phase?: string;
  status?: string;
  appId?: string;
  failClass?: string | null;
  detail?: string;
  exitCode?: number;
}

/** The filtered orchestrator state (image-jobs-get). */
export interface LabJob {
  jobId: string;
  state: string;
  outcome?: string | null;
  failClass?: string | null;
  failDetail?: string | null;
  recipes?: string[];
  /** lab.ps1's catalog write-back: 'ok verified=… skipped=…' or 'error: …' (#1710). */
  writeBack?: string | null;
  bundles: string[];
}

async function unwrap<T>(p: Promise<{ data: ApiResponse<T> }>, fallback: string): Promise<T> {
  const response = await p;
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || fallback);
  }
  return response.data.data;
}

const seg = encodeURIComponent;

export const appsApi = {
  list(): Promise<AppSummary[]> {
    return unwrap(apiClient.get<ApiResponse<AppSummary[]>>('/apps'), 'Katalog konnte nicht geladen werden');
  },

  versions(appId: string): Promise<AppRecipe[]> {
    return unwrap(apiClient.get<ApiResponse<AppRecipe[]>>(`/apps/${seg(appId)}`), 'App konnte nicht geladen werden');
  },

  /** 'create' = POST (409 if the version exists), 'update' = PUT to the version's own route. */
  upsert(input: AppRecipeInput, mode: 'create' | 'update'): Promise<AppRecipe> {
    const request =
      mode === 'create'
        ? apiClient.post<ApiResponse<AppRecipe>>('/apps', input)
        : apiClient.put<ApiResponse<AppRecipe>>(`/apps/${seg(input.appId)}/${seg(input.version)}`, input);
    return unwrap(request, 'Rezept konnte nicht gespeichert werden');
  },

  verify(appId: string, version: string, costCenter: string): Promise<VerifyResult> {
    return unwrap(
      apiClient.post<ApiResponse<VerifyResult>>(`/apps/${seg(appId)}/${seg(version)}/verify`, { costCenter }),
      'Prüfung konnte nicht gestartet werden'
    );
  },

  job(jobId: string): Promise<LabJob> {
    return unwrap(apiClient.get<ApiResponse<LabJob>>(`/image-jobs/${seg(jobId)}`), 'Lab-Job konnte nicht geladen werden');
  },

  events(jobId: string, after = 0): Promise<{ events: LabEvent[]; count: number }> {
    return unwrap(
      apiClient.get<ApiResponse<{ events: LabEvent[]; count: number }>>(`/image-jobs/${seg(jobId)}/events`, { params: { after } }),
      'Events konnten nicht geladen werden'
    );
  },

  /** A 15-minute read link to the diagnosis bundle of a failed recipe. */
  async bundleUrl(jobId: string, appId: string): Promise<string> {
    const data = await unwrap(
      apiClient.get<ApiResponse<{ url: string; expiresInMinutes: number }>>(`/image-jobs/${seg(jobId)}/bundles/${seg(appId)}`),
      'Bundle nicht gefunden'
    );
    return data.url;
  },
};

/** The rule errors of a 400 from POST/PUT (`details.errors`, same messages as the lab agent). */
export function recipeErrors(err: unknown): string[] {
  const data = (err as { response?: { status?: number; data?: { details?: { errors?: unknown } } } })?.response;
  const errors = data?.status === 400 ? data.data?.details?.errors : undefined;
  return Array.isArray(errors) ? errors.filter((e): e is string => typeof e === 'string') : [];
}
